/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import styled from 'styled-components';
import { InfogramContainer } from './infogram';
import AffilizzEmbed from './affilizz-embed.content';
import type { EmbedAffilizzBoxProps, EmbedAffilizzCtaProps } from './affilizz-embed.content';
import SocialEmbed from './social-embed.content';
import WebContainer from './web';

const IFrameStyled = styled.iframe`
  border: none;
  overflow: hidden;
  margin: auto;
`;

export type EmbedPluginProps = {
  src: string;
  embedTitle?: string;
  embedDescription?: string;
  embedImageUrl?: string;
  isAffilizz?: boolean;
  type?: 'social' | 'affilizz' | 'iframe';
  embedAffilizz?: EmbedAffilizzCtaProps | EmbedAffilizzBoxProps;
  eventCta?: (shopName?: string, price?: string, position?: string, label?: string) => void;
  width?: number;
  height?: number;
};

interface Renderer {
  handles: (state: EmbedPluginProps) => boolean;
  mediaCell: boolean;
  Component: React.FC<EmbedPluginProps>;
}

const Iframe = ({ src, width, height }: EmbedPluginProps) => {
  return <IFrameStyled src={src} loading="lazy" width={width ?? '100%'} height={height ?? 500} />;
};

const Renderers: Renderer[] = [
  {
    handles: (state): boolean => {
      return state.type === 'iframe';
    },
    mediaCell: false,
    Component: Iframe,
  },
  {
    handles: (state): boolean => {
      const regex = /https?:\/\/[^/]*infogram.com\/.+/;

      return regex.test(state.src);
    },
    mediaCell: true,
    Component: InfogramContainer,
  },
  {
    handles: (state): boolean => {
      const regex = /https:\/\/[^/]*twitter.com\/.+?/;

      return regex.test(state.src);
    },
    mediaCell: true,
    Component: SocialEmbed,
  },
  {
    handles: (state): boolean => {
      const regex = /https:\/\/x.com\/.+?/;

      return regex.test(state.src);
    },
    mediaCell: true,
    Component: SocialEmbed,
  },
  {
    handles: (state): boolean => {
      const regex = /https:\/\/[^/]*tiktok.com\/.+?\/video\/[0-9+]+/;

      return regex.test(state.src);
    },
    mediaCell: true,
    Component: SocialEmbed,
  },
  {
    handles: (state): boolean => {
      const regex = /https:\/\/[^/]*docs.google.com\/forms\/.*/;

      return regex.test(state.src);
    },
    mediaCell: true,
    Component: SocialEmbed,
  },
  {
    handles: (state: EmbedPluginProps): boolean => {
      const regex = /https:\/\/[^/]*bsky.app\/.*/;

      return regex.test(state.src);
    },
    mediaCell: true,
    Component: SocialEmbed,
  },
  {
    handles: (state): boolean => {
      return !!state?.isAffilizz;
    },
    mediaCell: false,
    Component: AffilizzEmbed,
  },
];

const MediaCell = styled.div`
  margin: var(--spacer-s) 0 var(--spacer);
  min-height: 380px;
`;

const EmbedCell = styled.div`
  max-width: 100%;
`;

const EmbedPlugin = (state: EmbedPluginProps) => {
  const { src, embedDescription, embedTitle, embedImageUrl } = state;
  if (src?.includes('clubic.com')) {
    return (
      <MediaCell>
        <WebContainer
          embedDescription={embedDescription}
          embedTitle={embedTitle}
          embedImageUrl={embedImageUrl}
          url={src}
        />
      </MediaCell>
    );
  }

  const currentRenderer = Renderers.find((renderer) => renderer.handles(state));

  if (currentRenderer && currentRenderer.mediaCell) {
    return (
      <MediaCell>
        <currentRenderer.Component {...state} />
      </MediaCell>
    );
  }

  if (currentRenderer && !currentRenderer.mediaCell) {
    return (
      <EmbedCell>
        <currentRenderer.Component {...state} />
      </EmbedCell>
    );
  }

  return null;
};

export default EmbedPlugin;
